import { useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  Heart,
  MessageCircle,
  MoreHorizontal,
  School,
  Users,
} from "lucide-react";
import { Dropdown } from "../../components/ui";
import ActivityAvatar from "../activities/ActivityAvatar";
import {
  activityTitle,
  authorName,
  engagementSummary,
  feedContent,
  postTypeLabel,
  resolvePostActivity,
  targetsSummary,
} from "./postHelpers";
import PostMediaGrid from "./PostMediaGrid";
import PostEngagementDrawer from "./PostEngagementDrawer";

dayjs.extend(relativeTime);

export default function PostFeedCard({ post, activities = [], onDelete }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [engageOpen, setEngageOpen] = useState(false);
  const content = feedContent(post.content);
  const isLong = content.length > 280;
  const visibleContent = expanded || !isLong ? content : `${content.slice(0, 280).trim()}…`;
  const { likes, comments } = engagementSummary(post);
  const author = post.author;
  const activity = resolvePostActivity(post, activities);
  const TypeIcon = post.type === "CLASS" ? School : Users;

  const actionItems = [
    {
      key: "edit",
      label: <div style={{ padding: "8px 12px" }}>Edit</div>,
      onClick: () => navigate(`/posts/edit/${post._id}`),
    },
    {
      key: "engage",
      label: <div style={{ padding: "8px 12px" }}>Likes & comments</div>,
      onClick: () => setEngageOpen(true),
    },
    {
      key: "delete",
      label: <div style={{ padding: "8px 12px", color: "#e83038" }}>Delete</div>,
      onClick: () => onDelete(post),
    },
  ];

  return (
    <article className="posts-feed-card">
      <header className="posts-feed-head">
        <div className="posts-feed-author">
          <ActivityAvatar
            post={post}
            activity={activity}
            activities={activities}
            size="md"
            title={activityTitle(activity)}
          />

          <div className="posts-feed-author-copy">
            <div className="posts-feed-author-row">
              <strong>{activityTitle(activity)}</strong>
              <span className="posts-feed-dot" aria-hidden />
              <span className="posts-feed-time" title={dayjs(post.createdAt).format("MMMM D, YYYY h:mm A")}>
                {post.createdAt ? dayjs(post.createdAt).fromNow() : "—"}
              </span>
            </div>
            <div className="posts-feed-meta-row">
              <span className="posts-feed-by">by {authorName(author)}</span>
              <span className={`posts-feed-type is-${(post.type || "child").toLowerCase()}`}>
                <TypeIcon size={12} />
                {postTypeLabel(post.type)}
              </span>
              <span className="posts-feed-target">{targetsSummary(post)}</span>
            </div>
          </div>
        </div>

        <Dropdown menu={{ items: actionItems }} trigger={["click"]} placement="bottomRight">
          <button type="button" className="posts-feed-menu" aria-label="Post actions">
            <MoreHorizontal size={18} />
          </button>
        </Dropdown>
      </header>

      {content ? (
        <div className="posts-feed-body">
          <p className="posts-feed-text">{visibleContent}</p>
          {isLong ? (
            <button type="button" className="posts-feed-more" onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? "Show less" : "Read more"}
            </button>
          ) : null}
        </div>
      ) : null}

      <PostMediaGrid post={post} />

      <footer className="posts-feed-foot">
        <button
          type="button"
          className="posts-feed-stats is-button"
          onClick={() => setEngageOpen(true)}
        >
          {likes > 0 ? (
            <span className="posts-feed-stat">
              <Heart size={14} />
              {likes}
            </span>
          ) : null}
          {comments > 0 ? (
            <span className="posts-feed-stat">
              <MessageCircle size={14} />
              {comments} {comments === 1 ? "comment" : "comments"}
            </span>
          ) : null}
          {likes === 0 && comments === 0 ? (
            <span className="posts-feed-stat is-muted">No likes or comments yet</span>
          ) : null}
        </button>

        <button type="button" className="posts-feed-view" onClick={() => setEngageOpen(true)}>
          Likes & comments
        </button>
      </footer>

      <PostEngagementDrawer
        post={post}
        open={engageOpen}
        onClose={() => setEngageOpen(false)}
      />
    </article>
  );
}
